import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { campAPI, reviewAPI } from '../lib/api';
import { useAuth } from '../lib/auth-context';
import { TrustBadge } from '../components/TrustBadge';
import ReviewForm from '../components/ReviewForm';
import { ShareButton } from '../components/ShareButton';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { 
  MapPin, 
  Mail, 
  Phone, 
  ArrowLeft,
  Star,
  Globe,
  Users,
  Calendar,
  Tent,
  ExternalLink,
  Eye
} from 'lucide-react';
import { formatDate, reviewerRoles } from '../lib/utils';

// Helper to get reviewer role label
const getReviewerRoleLabel = (roleValue) => {
  const role = reviewerRoles.find(r => r.value === roleValue);
  return role?.label || roleValue;
};

const CampDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [camp, setCamp] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for preview mode
  const searchParams = new URLSearchParams(window.location.search);
  const isPreviewMode = searchParams.get('preview') === 'true';

  // Check if current user owns this profile
  const isOwner = user && camp && camp.user_id === user.id;

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [campResponse, reviewsResponse] = await Promise.all([
        campAPI.get(id),
        reviewAPI.list({ target_type: 'camp', target_id: id })
      ]);
      setCamp(campResponse.data);
      setReviews(reviewsResponse.data);
    } catch (err) {
      setError('Failed to load camp profile.');
      console.error('Error:', err);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-20 md:pb-0">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
          <Skeleton className="h-8 w-32 mb-8" />
          <Skeleton className="aspect-[21/9] rounded-3xl mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-10 w-3/4" />
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-24 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !camp) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive mb-4">{error || 'Camp not found'}</p>
          <Button asChild>
            <Link to="/directory/camps">Back to Directory</Link>
          </Button>
        </div>
      </div>
    );
  }

  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  return (
    <div className="min-h-screen pb-20 md:pb-0">
      {/* Preview Mode Banner */}
      {isPreviewMode && (
        <div className="bg-primary text-primary-foreground py-3 px-6 text-center" data-testid="preview-banner">
          <div className="container mx-auto flex items-center justify-center gap-3">
            <Eye className="w-5 h-5" />
            <span className="font-medium">This is a preview of your public profile</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => window.close()}
              className="ml-4"
            >
              Close Preview
            </Button>
          </div>
        </div>
      )}
      
      {/* Hero Image */}
      <div className="relative h-64 md:h-96 bg-muted">
        <img
          src={camp.images?.[0] || 'https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=1200'}
          alt={camp.camp_name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        {/* Back button overlay */}
        <div className="absolute top-6 left-6">
          <Link 
            to="/directory/camps"
            className="inline-flex items-center gap-2 px-4 py-2 bg-background/90 backdrop-blur-sm rounded-full text-sm font-medium hover:bg-background transition-colors"
            data-testid="back-link"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>

        {/* Badge overlay */}
        <div className="absolute bottom-6 left-6">
          <TrustBadge status={camp.verification_status} size="large" />
        </div>
      </div>

      <div className="container mx-auto px-6 md:px-12 lg:px-24 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header */}
            <div>
              <div className="flex items-start justify-between gap-4 mb-2">
                <h1 className="font-heading font-bold text-3xl md:text-4xl text-foreground">
                  {camp.camp_name}
                </h1>
                <ShareButton type="camps" id={camp.id} title={`${camp.camp_name} — Campia`} className="shrink-0 mt-1" />
              </div>
              <div className="flex flex-wrap gap-4 text-muted-foreground">
                <span className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {camp.nearest_town}, {camp.province}
                </span>
                <span className="flex items-center gap-1 capitalize">
                  <Tent className="w-4 h-4" />
                  {camp.camp_type}
                </span>
                {averageRating && (
                  <span className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                    {averageRating} ({reviews.length} reviews)
                  </span>
                )}
              </div>
            </div>

            {/* Description */}
            {camp.description && (
              <div className="bg-card rounded-2xl border border-border p-6">
                <h2 className="font-heading font-semibold text-xl mb-4">About</h2>
                <p className="text-muted-foreground leading-relaxed">
                  {camp.description?.replace('[DEMO] ', '')}
                </p>
              </div>
            )}

            {/* Activities */}
            {camp.activities?.length > 0 && (
              <div className="bg-card rounded-2xl border border-border p-6">
                <h2 className="font-heading font-semibold text-xl mb-4">Activities Offered</h2>
                <div className="flex flex-wrap gap-2">
                  {camp.activities.map((activity) => (
                    <span 
                      key={activity}
                      className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-medium"
                    >
                      {activity}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Gallery */}
            {camp.images?.length > 1 && (
              <div className="bg-card rounded-2xl border border-border p-6">
                <h2 className="font-heading font-semibold text-xl mb-4">Gallery</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {camp.images.map((img, idx) => (
                    <div key={idx} className="aspect-video rounded-xl overflow-hidden">
                      <img src={img} alt={`${camp.camp_name} ${idx + 1}`} className="w-full h-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Reviews Section */}
            <div className="bg-card rounded-2xl border border-border p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-heading font-semibold text-xl">Reviews</h2>
                {/* Show review form for logged-in reviewers/facilitators who don't own this profile (hidden in preview mode) */}
                {!isPreviewMode && user && !isOwner && (user.role === 'reviewer' || user.role === 'facilitator') && (
                  <ReviewForm
                    targetType="camp"
                    targetId={id}
                    targetName={camp.camp_name}
                    userRole={user.role}
                    onSuccess={loadData}
                  />
                )}
              </div>
              
              {reviews.length > 0 ? (
                <div className="space-y-6">
                  {reviews.map((review) => (
                    <div key={review.id} className="border-b border-border pb-6 last:border-0 last:pb-0">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-medium">{review.reviewer_name}</span>
                          {review.reviewer_role && (
                            <span className="text-muted-foreground text-sm ml-2">
                              — {getReviewerRoleLabel(review.reviewer_role)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted'}`}
                            />
                          ))}
                        </div>
                      </div>
                      <p className="text-muted-foreground">
                        {review.review_text?.replace('[DEMO] ', '')}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No reviews yet. Be the first to share your experience!
                </p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* Quick Info Card */}
              <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
                <h3 className="font-heading font-semibold text-lg">Quick Info</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">Age Range</div>
                      <div className="font-medium">{camp.age_range_min} - {camp.age_range_max} years</div>
                    </div>
                  </div>

                  {camp.capacity && (
                    <div className="flex items-center gap-3">
                      <Users className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="text-sm text-muted-foreground">Capacity</div>
                        <div className="font-medium">~{camp.capacity} campers</div>
                      </div>
                    </div>
                  )}

                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-muted-foreground" />
                    <div>
                      <div className="text-sm text-muted-foreground">Availability</div>
                      <div className="font-medium capitalize">{camp.seasonality?.replace('_', ' ')}</div>
                    </div>
                  </div>

                  {camp.accreditation && (
                    <div className="flex items-center gap-3">
                      <Star className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="text-sm text-muted-foreground">Accreditation</div>
                        <div className="font-medium">{camp.accreditation}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Contact Card */}
              <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
                <h3 className="font-heading font-semibold text-lg">Contact</h3>
                
                <div className="space-y-2">
                  {camp.contact_email && (
                    <Button 
                      asChild 
                      className="w-full rounded-full"
                      data-testid="contact-email-btn"
                    >
                      <a href={`mailto:${camp.contact_email}`}>
                        <Mail className="w-4 h-4 mr-2" />
                        Email
                      </a>
                    </Button>
                  )}
                  {camp.contact_phone && (
                    <Button 
                      asChild 
                      variant="outline" 
                      className="w-full rounded-full"
                      data-testid="contact-phone-btn"
                    >
                      <a href={`tel:${camp.contact_phone}`}>
                        <Phone className="w-4 h-4 mr-2" />
                        Call
                      </a>
                    </Button>
                  )}
                  {camp.website && (
                    <Button 
                      asChild 
                      variant="outline" 
                      className="w-full rounded-full"
                      data-testid="website-btn"
                    >
                      <a href={camp.website} target="_blank" rel="noopener noreferrer">
                        <Globe className="w-4 h-4 mr-2" />
                        Website
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </a>
                    </Button>
                  )}
                </div>
              </div>

              {/* Social Links */}
              {camp.social_links && Object.keys(camp.social_links).length > 0 && (
                <div className="bg-card rounded-2xl border border-border p-6 space-y-4">
                  <h3 className="font-heading font-semibold text-lg">Social Media</h3>
                  <div className="space-y-2">
                    {Object.entries(camp.social_links).map(([platform, handle]) => (
                      <a
                        key={platform}
                        href={`https://${platform}.com/${handle}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                      >
                        <span className="capitalize">{platform}</span>
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Last Updated */}
              <p className="text-sm text-muted-foreground text-center">
                Last updated: {formatDate(camp.last_updated)}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampDetail;
